// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { newGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import ArrowRight from "@insite/mobius/Icons/ArrowRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { useAnalytics } from "@store/Framework/Common/Hooks/useAnalytics";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
    links = "links",
    content = "content",
    titleLink = "titleLink",
    viewDetails = "viewDetails",
    overrideTitle = "overrideTitle",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.titleLink]: LinkFieldValue;
        [fields.viewDetails]: LinkFieldValue;
        [fields.overrideTitle]: string;
        [fields.title]: string;
    };
}

interface LinkModel {
    fields: {
        title: string;
        imageUrl: string;
        altText: string;
        imageLink: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        content: string;
    };
}

export interface CatalogStyles {
    wrapper?: GridContainerProps;
    imageWrapper?: GridItemProps;
    linkWrapper: GridItemProps;
    title?: LinkPresentationProps;
    images?: InjectableCss;
    titleWrapper?: GridItemProps;
    ArrowRightWrapper?: InjectableCss;
    typoProps?: InjectableCss;
    iconWrapper?: InjectableCss;
    detailsWrapper?: TypographyPresentationProps;
    catalogImage?: LazyImageProps;
}

export const catalogStyles: CatalogStyles = {
    wrapper: {
        gap: 0,
    },

    imageWrapper: {
        width: 12,
        css: css`
            display: flex;
            border-bottom: ${pxToRem(1)} solid #eaeaea;
            justify-content: center;
            width: 100%;

            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        justify-content: flex-start;
                    `,
                    css`
                        width: 100%;
                        justify-content: flex-start;
                    `,
                    css`
                        width: 100%;
                        justify-content: flex-start;
                    `,
                ]);
            }}
        `,
    },
    linkWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
        `,
    },
    title: {
        css: css`
            ${linkStyles("tertiary.dark", "primary", false, true)}
            padding:${pxToRem(16, 0)};
            width: 100%;
            &:hover {
                text-decoration: none;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: ${pxToRem(20, 20)};
                    `,
                    css`
                        padding: ${pxToRem(20, 20)};
                    `,
                    css`
                        padding: ${pxToRem(16, 0)};
                    `,
                ]);
            }};
        `,
    },
    images: {
        css: css`
            display: flex;
            flex-direction: row;
        `,
    },
    ArrowRightWrapper: {
        css: css`
            color: red !important;
            padding: ${pxToRem(10)};
        `,
    },
    titleWrapper: {
        width: 12,
        css: css`
            font-weight: bold;
            display: flex;
            align-content: center;
            justify-content: center;
            border-bottom: ${pxToRem(1)} solid #eaeaea;
        `,
    },
    typoProps: {
        css: css`
            font-weight: bold;
        `,
    },
    detailsWrapper: {
        css: css`
            padding-top: ${pxToRem(10)};
            ${linkStyles("tertiary.dark", "primary", false, true)};
            text-transform: none !important;
            font-weight: bold;
            &:hover {
                text-decoration: none;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: ${pxToRem(20, 20)};
                    `,
                    css`
                        padding: ${pxToRem(20, 20)};
                    `,
                    css`
                        padding: ${pxToRem(16, 0)};
                    `,
                ]);
            }};
        `,
    },
    iconWrapper: {
        css: css`
            position: relative;
            top: ${pxToRem(5)};
            left: ${pxToRem(10)};
        `,
    },
    catalogImage: {
        css: css`
            width: ${pxToRem(70)};
            min-width: ${pxToRem(70)};
            padding: unset;
            background: none;
            margin-right: ${pxToRem(28)};
        `,
    },
};

const styles = catalogStyles;

const CatalogListGrid = ({ fields }: OwnProps) => {
    const titleLink = useGetLink(fields.titleLink);
    const detailsLink = useGetLink(fields.viewDetails);

    return (
        <GridContainer {...styles.wrapper}>
            {fields.title && (
                <GridItem {...styles.titleWrapper}>
                    <Link {...styles.title} href={titleLink.url}>
                        {fields.title}
                    </Link>
                </GridItem>
            )}
            {fields.links.map(link => (
                <GridItem key={newGuid()} {...styles.imageWrapper}>
                    <LinkView link={link} />
                </GridItem>
            ))}
            {detailsLink.url && (
                <GridItem width={12}>
                    <Link {...styles.detailsWrapper} href={detailsLink.url}>
                        {fields.overrideTitle || detailsLink.title}
                        <Typography {...styles.iconWrapper}>
                            <Icon src={ArrowRight} />
                        </Typography>
                    </Link>
                </GridItem>
            )}
        </GridContainer>
    );
};

type LinkViewProps = {
    link: LinkModel;
};

const LinkView = ({ link }: LinkViewProps) => {
    const prodURL = useGetLink(link.fields.destination);
    const prodTitle = link.fields.title;
    const { dispatchProductCatalogEvent } = useAnalytics();

    const handleCatalogClick = () => {
        dispatchProductCatalogEvent(prodTitle, prodURL.url);
    };

    return (
        <Link
            onClick={handleCatalogClick}
            {...styles.title}
            href={prodURL.url}
            target={link.fields.openInNewWindow ? "_blank" : "_self"}
        >
            <StyledWrapper {...styles.images}>
                {link.fields.imageUrl && (
                    <LazyImage src={link.fields.imageUrl} altText={link.fields.altText} {...styles.catalogImage} />
                )}
                <StyledWrapper {...styles.linkWrapper}>
                    <Typography {...styles.typoProps}>{link.fields.title || prodURL.title}</Typography>
                    <StyledWrapper>{parse(link.fields.content, parserOptions)}</StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </Link>
    );
};

const definition: WidgetDefinition = {
    group: "Basic",
    icon: "list",
    fieldDefinitions: [
        {
            fieldType: "General",
            name: fields.titleLink,
            displayName: "Title Link",
            editorTemplate: "LinkField",
            defaultValue: { type: "Page", value: "" },
        },
        {
            name: fields.title,
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
        },
        {
            name: fields.links,
            editorTemplate: "ListField",
            getDisplay: (item: HasFields) => {
                return item.fields.title;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "destination",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Url",
                    },
                    isRequired: true,
                },
                {
                    name: "openInNewWindow",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
                {
                    name: fields.imageUrl,
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                },
                {
                    name: fields.altText,
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: fields.imageLink,
                    editorTemplate: "LinkField",
                    defaultValue: { type: "Page", value: "" },
                },
                {
                    name: fields.content,
                    displayName: "Content",
                    editorTemplate: "RichTextField",
                    defaultValue: "",
                },
            ],
        },
        {
            fieldType: "General",
            name: fields.viewDetails,
            displayName: "ViewDetails",
            editorTemplate: "LinkField",
            defaultValue: { type: "Page", value: "" },
        },
        {
            name: fields.overrideTitle,
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: CatalogListGrid,
    definition,
};

export default widgetModule;
